import * as Utils from '@js/modules/utils';
import History from '@js/modules/history';

import {fetchSearch} from '@js/actions/searchActions';

import {searchStore} from '@js/stores/searchStore';
import {mapStore} from '@js/stores/mapStore';


class SearchManager {
    _searchHistoryName = 'search_data';

    _mapLoaded = false;

    _mapLoadedCallback = undefined;

    _searchChangeCallback = undefined;

    _searchedLocation = null;

    /* Public methods
    ******************** */
    initialize = (searchCallback) => {
        this._watchMapLoad();

        if (searchCallback) {
            this._searchChangeCallback = searchCallback;
        }

        const previousSearchData = History.getPreviousState(this._searchHistoryName, {useUrlParams: true});

        if (Utils.isPresent(previousSearchData)) {
            this._setInitialFilters(previousSearchData);

            this._performSearch(previousSearchData, {ignoreHistory: true});

            if (this._searchChangeCallback) {
                this._searchChangeCallback({
                    query: previousSearchData.query,
                    queryDisplay: previousSearchData.queryDisplay
                });
            }
        }

        this._onHistoryChange((newSearchData) => {
            this._performSearch(newSearchData, {ignoreHistory: true});

            if (this._searchChangeCallback) {
                this._searchChangeCallback({
                    query: newSearchData.query,
                    queryDisplay: newSearchData.queryDisplay
                });
            }
        });
    };

    fetchSearch = (params, searchOptions = {}) => {
        searchStore.isSearching = true;

        const searchParams = {
            query: params.query,
            queryDisplay: params.queryDisplay,
            ...params.filters
        };

        if (params.location && (params.location.bbox || params.location.center)) {
            searchParams.location = {
                bbox: !!params.location.bbox ? [...params.location.bbox] : null,
                center: !!params.location.center ? [...params.location.center] : null
            };
        }

        if (!searchOptions.ignoreHistory) {
            History.saveCurrentState(
                {
                    [this._searchHistoryName]: searchParams
                },
                searchParams,
                {
                    keepPreviousParams: false,
                    replaceOnly: Utils.isPresent(params.filters)
                }
            );
        }

        return this._performSearch(searchParams);
    };

    clearSearch = () => {
        searchStore.isSearching = false;
        searchStore.ridesLoadedFromMap = true;

        searchStore.query = null;
        searchStore.queryDisplay = null;

        searchStore.ridesData = [];

        searchStore.selectedFilters = [];
        searchStore.availableFilters = [];

        History.saveCurrentState(
            {},
            {}
        );

        this._showResultsOnMap();
    };

    /* Private Methods
    ******************** */
    _watchMapLoad = () => {
        mapStore.subscribe((mapState) => {
            if (mapState.map && !this._mapLoaded) {
                mapStore.map.onMapLoaded(() => {
                    this._mapLoaded = true;

                    if (this._mapLoadedCallback) {
                        setTimeout(() => {
                            this._mapLoadedCallback();
                            this._mapLoadedCallback = undefined;
                        }, 300);
                    }

                    this._listenMapEvents();
                });
            }
        });
    };

    _setInitialFilters = (initialFilters) => {
        if (!initialFilters) {
            return;
        }

        searchStore.selectedFilters = {
            rideTypes: initialFilters.rideTypes ? new Set(initialFilters.rideTypes) : null,
            levels: initialFilters.levels ? new Set(initialFilters.levels) : null,
            distance: initialFilters.distance ? new Set(initialFilters.distance) : null,
            duration: initialFilters.duration ? new Set(initialFilters.duration) : null
        };
    };

    _onHistoryChange = (onHistoryCallback) => {
        History.onStateChange((newState) => {
            if (newState[this._searchHistoryName]) {
                onHistoryCallback(newState[this._searchHistoryName]);
            }
        });
    };

    _performSearch = (searchParams) => {
        searchStore.ridesLoadedFromMap = false;
        this._searchedLocation = searchParams.location || null;

        return fetchSearch(searchParams)
            .then((response) => {
                searchStore.isSearching = false;
                searchStore.query = searchParams.query;
                searchStore.queryDisplay = searchParams.queryDisplay;
                // searchStore.selectedFilters = searchParams.filters || [];

                if (response) {
                    searchStore.ridesData = response.rides || [];

                    searchStore.visibleRideIds = response.rides?.map((ride) => ride.id) || [];

                    searchStore.availableFilters = response.aggregations?.rides || [];

                    this._onMapLoaded(this._showResultsOnMap);
                } else {
                    searchStore.ridesData = [];

                    searchStore.visibleRideIds = [];

                    searchStore.availableFilters = [];
                }

                return searchParams;
            });
    };

    _listenMapEvents = () => {
        if (!mapStore.map) {
            return;
        }

        if (typeof mapStore.map.onVisibleMarkersChanged === 'function') {
            mapStore.map.onRidesChanged((ridesData) => {
                searchStore.isSearching = false;
                // searchStore.ridesLoadedFromMap = true;
                searchStore.ridesData = ridesData;
            });

            mapStore.map.onVisibleMarkersChanged((markers) => {
                searchStore.visibleRideIds = markers
                    .filter((marker) => marker.type === 'ride')
                    .map((marker) => marker.id);
            });

            mapStore.map.onRideMarkerClick((marker) => {
                searchStore.highlightedRideId = marker?.id;
            });

            mapStore.map.onRideMarkerHover((marker) => {
                searchStore.highlightedRideId = marker?.id;
            });
        }
    };

    _showResultsOnMap = () => {
        if (!mapStore.map) {
            return;
        }

        if (searchStore.ridesLoadedFromMap) {
            mapStore.map.toggleSearchOnZoom(true);
            mapStore.map.fetchSelected();

            return;
        } else {
            mapStore.map.toggleSearchOnZoom(false);
        }

        mapStore.map.showSelected({
            ridesData: searchStore.ridesData,
            filter: searchStore.filters,
            location: this._searchedLocation,
            clearBounds: true
        });
    };

    _onMapLoaded = (callback) => {
        if (!this._mapLoaded) {
            this._mapLoadedCallback = callback;
        } else {
            callback();
        }
    };
}

export default new SearchManager();
